import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { Chess } from 'chess.js';
import { NgxChessBoardView, PieceIconInput } from 'ngx-chess-board';
import { BehaviorSubject } from 'rxjs';
import { ChessComService } from 'src/app/services/chess-com.service';
import { BestMoveParams, Game, GameState, StockfishService } from 'src/app/services/stockfish.service';

export class BoardSettings {
  size: number = 350;
  showCoords: boolean;
  showLastMove: boolean;
  lightDisabled: boolean = false;
  darkDisabled: boolean = false;
  pieceIcons: PieceIconInput
}
export class Move {
  number: number;
  white: string;
  black: string;
}

export enum GameResult {
  BLACK_WON = 'BLACK_WON',
  WHITE_WON = 'WHITE_WON',
  DRAW = 'DRAW'
}
@Component({
  selector: 'chess',
  templateUrl: './chess.component.html',
  styleUrls: ['./chess.component.css'],
})
export class ChessComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('board', { static: false }) board: NgxChessBoardView;
  @ViewChild('container', { static: false, read: ElementRef }) boardEl!: ElementRef;

  game: Game;
  worker: Worker;
  settings: BoardSettings;
  moves: Move[] = [];
  timeoutId: any;
  params: BestMoveParams;
  chess: Chess;


  constructor(
    private cdr: ChangeDetectorRef,
    private chessComService: ChessComService,
    private stockFish: StockfishService
  ) {
  }

  async start() {
  
    if (this.game.status === GameState.PENDING && this.game.opponent === 'white') {
      const bot = await this.stockFish.getBestMover(this.params);
      this.game.score = bot.evaluation;
      this.game.eval = this.game.eval + this.game.score;

      const move = bot.move;
      this.board.move(move);
    }
  }
  evaluatePosition(fen: string, depth: number = 10) {
    this.worker.postMessage(`position fen ${fen}`);
    this.worker.postMessage(`go depth ${depth}`);
  }

  // Emits every 1000ms (1 second), maps to milliseconds remaining
  whiteTimer$: BehaviorSubject<number> = new BehaviorSubject<number>(60 * 5);

  // Emits every 1000ms (1 second), maps to milliseconds remaining
  blackTimer$: BehaviorSubject<number> = new BehaviorSubject<number>(60 * 5);

  onInputChange() {

    if (this.game.timer < 1) {
      this.game.timer = 1;
    }
    if (this.game.timer > 90) {
      this.game.timer = 90;
    }
    this.whiteTimer$.next(this.game.timer * 60);
    this.blackTimer$.next(this.game.timer * 60);
  }
  resetTimer() {
    this.stopCounter();
    this.whiteTimer$ = new BehaviorSubject(this.game.timer * 60);
    this.blackTimer$ = new BehaviorSubject(this.game.timer * 60);
  }

  countDown() {
    

    this.timeoutId = setTimeout(() => {

      switch (this.game.active) {
        case 'white':
          this.game.whiteTimer--;
          this.whiteTimer$.next(this.game.whiteTimer);
          if (this.game.whiteTimer === 0) {
            this.game.status = GameState.ENDED;
            this.game.result = GameResult.BLACK_WON;
          }
          break;
        case 'black':
          this.game.blackTimer--;
          this.blackTimer$.next(this.game.blackTimer);
          if (this.game.blackTimer === 0) {
            this.game.status = GameState.ENDED;
            this.game.result = GameResult.WHITE_WON;
          }
          break;
      }
      this.countDown();
    }, 1000);

  }
  stopCounter() {
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  resign() {
    this.resetTimer();
    this.game.result = this.game.player === 'white' ? GameResult.BLACK_WON : GameResult.WHITE_WON;
    this.board.reset();
    this.chess.reset();
    this.game.status = GameState.PENDING;
  }
  async reverse() {
    if (this.game.status === GameState.PENDING) {
      switch (this.game.player) {
        case 'white':
          this.game.player = 'black';
          this.game.opponent = 'white';
          break;
        case 'black':
          this.game.player = 'white';
          this.game.opponent = 'black';
          break;
        default:
          this.game.player = 'white';
          this.game.opponent = 'black';
      }
    }
    this.board.reverse();
  }
  undo() {
    this.chess.undo();
    this.board.undo();
  }

  getMoveHistory() {
    return this.board.getMoveHistory();
  }
  async onMoveChange(event: any) {

    if (this.game.status === GameState.PENDING || this.game.status === GameState.PAUSED) {
      this.game.status = GameState.STARTED;
    }
    this.stopCounter();

    switch (event.color) {
      case 'white':
        this.game.active = 'black';
        break;
      case 'black':
        this.game.active = 'white'
        break;
      default:
        this.game.active = 'white';
    }

    this.countDown();

    this.chess.move(event.move);
    const history = this.chess.history({ verbose: true });
    const whiteMoves = history.filter(m => m.color === 'w');
    const blackMoves = history.filter(m => m.color === 'b');
    this.moves = whiteMoves.map((m, i) => {

      return {
        number: i,
        white: m.san,
        black: blackMoves[i]?.san || ''
      }
    });
    this.params.binc = 0;
    this.params.winc = 0;
    this.params.btime = this.game.blackTimer * 1000;
    this.params.wtime = this.game.whiteTimer * 1000;
    this.params.depth = 5;
    this.params.movetime = 2 * 1000;
    this.params.fen = this.board.getFEN();

    if (this.botMove(event)) {

      const bot = await this.stockFish.getBestMover(this.params);
      this.game.score = bot.evaluation;
      this.game.eval = bot.evaluation;

      const move = bot.move;
      this.board.move(move);
    }
  }


  private fit(): void {
    const avail = this.boardEl.nativeElement.clientWidth;
    this.settings.size = avail < 800 ? 350 : 700;
  }


  ngOnInit(): void {
    this.chess = new Chess();
    this.game = new Game();
    this.settings = new BoardSettings();
    this.params = new BestMoveParams();
    this.whiteTimer$.next(this.game.timer * 60);
    this.blackTimer$.next(this.game.timer * 60);


  }

  botMove(move: any) {
    return this.game.player === move.color;
  }

  ngAfterViewInit(): void {
    /*
    angular need to detect changes in the component annd or what ever reason it
    does not detect them unless this ChangeDetectorRef is included in this lifecycle

    this work arounud was suggested by chat gpt.
    */
    this.fit();
    this.cdr.detectChanges();

  }
  ngOnDestroy(): void {
    this.worker.terminate();
    this.stopCounter();
  }
}
